import React from 'react';
import { Download, WarningAmber } from '@mui/icons-material';
import { Stack, FormControlLabel, Link, Switch, Box } from "@mui/material";
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { GridRenderCellParams, GridColDef, GridCellParams, GridColumnHeaderParams } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { Report } from '../../types/cutterHistory';
import LogToDB from '../../types/dbLogs';
import { getZipFileLink, getFormatedStringFromDate } from '../../utils/dateUtils';
import { RootState } from '../../store';
import { toggleStatusReportsFilter } from '../../redux/reportFilter';
import CutterTooltip from '../../common/CutterTooltip';

// Component for handling file download with archiving policy
interface FileDownloadCellProps {
    zipUploadDate: Date;
    zipFileName: string;
    serialNumber: string;
    zipStoredOnDisk: boolean;
}

const FileDownloadCell: React.FC<FileDownloadCellProps> = ({ zipUploadDate, zipFileName, serialNumber, zipStoredOnDisk }) => {
    const formattedFileName = formatZipFileName(serialNumber, zipFileName);
    
    if (zipStoredOnDisk) {
        // File is less than 3 months old - show downloadable link
        return (
            <Link 
                sx={{ textDecoration: 'none' }}
                href={getZipFileLink(zipFileName, zipStoredOnDisk)}
                download 
                color="inherit"
            >
                {formattedFileName}
                &nbsp;&nbsp;
                <Download 
                    onClick={() => onDownload(formattedFileName)} 
                    sx={{ color: grey[800], fontSize: '1em', bottom: '0px' }} 
                />
            </Link>
        );
    } else {
        // File is older than 45 days - show filename only (archived)
        return (
            <span style={{ color: grey[600], fontStyle: 'italic' }}>{formattedFileName}&nbsp;(archived)</span>
        );
    }
};


function formatShared(value: number): any {
    switch (value) {
        case 1:
            return 'All on cutter';
        case 2:
            return 'Shared Bibliobus';
        case 3:
            return 'Shared Biblio & EvM';
        default:
            return ''
    }
};

function formatZipFileName(serialNumber: string, zipFile: string): string {
    return zipFile.
        replace(`${serialNumber}_`, '').
        replace('_Encrypted.zip', '').
        replace('_', ' ').
        replace('.zip', ' ')
}

const onDownload: any = async (target: string)  => {
    // onClick={(async () => await onDownload(formatZipFileName(cutterReport.row.serialNumber, cutterReport.row.zipFileName)))}
    await LogToDB('download', target);

}

// Helper function to compare version strings (simple lexicographic comparison for now)
function compareVersions(version1: string, version2: string): number {
    if (!version1 || !version2) return 0;
    
    // Split versions by dots and compare numerically where possible
    const v1Parts = version1.split('.').map(part => {
        const num = parseInt(part, 10);
        return isNaN(num) ? part : num;
    });
    const v2Parts = version2.split('.').map(part => {
        const num = parseInt(part, 10);
        return isNaN(num) ? part : num;
    });
    
    const maxLength = Math.max(v1Parts.length, v2Parts.length);
    
    for (let i = 0; i < maxLength; i++) {
        const v1Part = v1Parts[i] || 0;
        const v2Part = v2Parts[i] || 0;
        
        if (v1Part < v2Part) return -1;
        if (v1Part > v2Part) return 1;
    }
    
    return 0;
}

// Helper function to create version columns with change highlighting
function createVersionColumn(field: string, headerName: string, flex: number = 1): GridColDef {
    return {
        field,
        headerName,
        flex,
        renderCell: (params: GridCellParams) => {
            const currentValue = params.value;
            let cellStyle: React.CSSProperties = {};
            
            try {
                // Get all row IDs and find current position
                const allRowIds = params.api.getAllRowIds();
                const currentRowIndex = allRowIds.findIndex((id: string | number) => id === params.id);


                
                // Get the next row (below current row) for comparison
                if (currentRowIndex >= 0 && currentRowIndex < allRowIds.length - 1) {
                    const nextRowId = allRowIds[currentRowIndex + 1];
                    const nextValue = params.api.getCellValue(nextRowId, field);
                    
                    if (currentValue && nextValue && currentValue !== nextValue) {
                        const comparison = compareVersions(String(currentValue), String(nextValue));
                        
                        if (comparison > 0) {
                            // Current version is higher than next (upgrade from next to current)
                            cellStyle = {
                                color: '#28a745', // Green for upgrade
                                fontWeight: 600,
                                fontFamily: 'inherit'
                            };
                        } else if (comparison < 0) {
                            // Current version is lower than next (downgrade from next to current)
                            cellStyle = {
                                color: '#dc3545', // Red for downgrade
                                fontWeight: 600,
                                fontFamily: 'inherit'
                            };
                        }
                    }
                }
            } catch (error) {
                // Fallback: if API methods are not available, just display normally
                console.warn('DataGrid API method not available:', error);
            }
            
            return (
                <span style={cellStyle}>
                    {String(currentValue || '')}
                </span>
            );
        }
    };
}

// Component for rendering the column header with filter switch
const ZipFileNameHeader: React.FC<GridColumnHeaderParams> = (params) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const showStatusReportsOnly = useSelector((state: RootState) => state.persistedReportFilterReducer.showStatusReportsOnly);

    const handleToggle : any = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(toggleStatusReportsFilter());
        event.stopPropagation();
    };

    return (
        <Stack direction="row" sx={{ justifyContent: "space-between",    alignItems: "center" }} >
            <span>{params.colDef.headerName}</span>
            <FormControlLabel     
                control={
                    <Switch
                        checked={showStatusReportsOnly}
                        onChange={e=>{e.preventDefault();handleToggle(e);}}
                        size="small"
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                }
                label={showStatusReportsOnly ? 'Status Reports only' : 'All reports'} 
                labelPlacement="end"
                sx={{ 
                    marginLeft: '3em',
                    marginTop: '0.3em',
                    gap: 0.5,
                    '& .MuiFormControlLabel-label': {
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary
                    }
                }}
            />
        </Stack>
    );
};

export function getReportsColumns(reports: Report[]): GridColDef[] {
    const hasEffectiveCuttingTime = reports.some(r => r.effectiveCuttingTime != null);

    const effectiveCuttingTimeCol: GridColDef = {
        field: "effectiveCuttingTime", minWidth: 100, headerName: "EffectiveCutT", flex: 1,
        valueFormatter: (value: number | undefined) => value != null ? value.toFixed(2) : ''
    };

    const columns: GridColDef[] = [
        /* { field: "extractDate", headerName: "Extract", flex: 1 }, */
        {
            field: "zipFileName",
            minWidth: 400,
            headerName: "File (Local time)",
            flex: 2,
            renderHeader: (params: GridColumnHeaderParams) => <ZipFileNameHeader {...params} />,
            renderCell: (cutterReport: GridRenderCellParams<Report, string>) => {
                const { zipUploadDate, zipFileName, serialNumber, zipStoredOnDisk, uploadDaysDelay } = cutterReport.row;
                const delay = Math.max(0, uploadDaysDelay ?? 0);
                const hasDelay = delay > 1;
                const tzIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const tzShort = new Intl.DateTimeFormat(navigator.language, { timeZoneName: 'short' })
                    .formatToParts(new Date())
                    .find(p => p.type === 'timeZoneName')?.value ?? '';
                const tooltipContent = (
                    <Box>
                        <div>Uploaded: {getFormatedStringFromDate(zipUploadDate)}</div>
                        <div style={{ fontSize: '0.8em', opacity: 0.7 }}>{tzShort} · {tzIana}</div>
                        {hasDelay && <div>Delay: {delay} days</div>}
                    </Box>
                );
                return (
                    <CutterTooltip title={tooltipContent} severity={hasDelay ? 'warning' : 'success'} placement="top"
                        copyText={`Uploaded: ${getFormatedStringFromDate(zipUploadDate)}${hasDelay ? ` | Delay: ${delay} days` : ''}`}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ overflow: 'hidden' }}>
                            {hasDelay && <WarningAmber sx={{ color: '#ffa726', fontSize: '1em', flexShrink: 0 }} />}
                            <FileDownloadCell
                                zipUploadDate={zipUploadDate}
                                zipFileName={zipFileName}
                                serialNumber={serialNumber}
                                zipStoredOnDisk={zipStoredOnDisk}
                            />
                        </Stack>
                    </CutterTooltip>
                );
            }
        },
        ...(hasEffectiveCuttingTime ? [effectiveCuttingTimeCol] : []),
        {
            field: "vacuumTime", minWidth: 100, headerName: "VacuumT", flex: 1,
            valueFormatter: (value: number | undefined) => value != null ? value.toFixed(2) : ''
        },
        {
            field: "zipComment", minWidth: 200, headerName: "Comment", flex: 3,
            renderCell: (params: GridRenderCellParams<Report, string>) => {
                const { zipErrorCode, zipErrorDate } = params.row;
                const comment = params.value ?? '';
                const hasError = !!zipErrorCode;
                if (!hasError && !comment) return null;

                const tooltipContent = (
                    <Box>
                        {comment && <div>{comment}</div>}
                        {hasError && <div style={{ marginTop: comment ? '0.4em' : undefined }}>Error: {zipErrorCode}</div>}
                        {hasError && zipErrorDate && <div>Date: {getFormatedStringFromDate(zipErrorDate)}</div>}
                    </Box>
                );
                const cell = (
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%',
                        ...(hasError ? { color: '#dc3545', fontWeight: 500 } : {}) }}>
                        {comment}
                    </Box>
                );
                return (
                    <CutterTooltip title={tooltipContent} severity={hasError ? 'error' : 'success'} placement="top"
                        copyText={[zipErrorDate ? `${getFormatedStringFromDate(zipErrorDate)}` : null, zipErrorCode ? `${zipErrorCode}` : null,  comment || null].filter(Boolean).join(' | ') || undefined}>
                        {cell}
                    </CutterTooltip>
                );
            }
        },
        /* { field: "yearWeek", headerName: "Year Week", flex: 1 } */
    ];

    return columns;
}

export const lisaColumns = [
    /* { field: "serialNumber", headerName: "Name", flex: 1}, */
    { field: "yearWeek", headerName: "Week", flex: 1 },
    { field: "lastReport", headerName: "Last report", maxWidth: 360, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    { field: "extractDate", headerName: "Reported on", maxWidth: 180, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    { field: "lastReportInDays", headerName: "Integration delay (Days)", flex: 1, valueFormatter: (value: number) => value },
    
];

export const softwareColumns = [
    /* { field: "rowNumber", headerName: "Rank", flex: 1, }, */
    {
        field: "fromDate", headerName: "Registered at", minWidth: 150, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value),
        description: 'This is not the installation date but the registration date in our systems'
    },
    /* { field: "todate", headerName: "To", flex: 1 }, */
    { field: "mainFeatureHostId", headerName: "HostID", flex: 1, valueFormatter: (value: string) => (value ?? "").replace('-1001', '') },
    createVersionColumn("commercialVersion", "Version"),
    createVersionColumn("cnuMVersion", "CNum"),
    createVersionColumn("cmachineVersion", "CMachine"),
    createVersionColumn("maintenanceVersion", "Maintenance"),
    createVersionColumn("pilotVersion", "Pilot"),
    createVersionColumn("bibliobusVersion", "Bibliobus"),
    createVersionColumn("eventManagerVersion", "EvM"),
    // { field: "alertsManagerVersion", headerName: "AlertMgr", flex: 1 },
    // { field: "statusReportManagerVersion", headerName: "StatusReportMgr", flex: 1 },
]

export const softwareVersalisColumns = [
    /* { field: "rowNumber", headerName: "Rank", flex: 1, }, */
    {
        field: "fromDate", headerName: "Registered at", minWidth: 80, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value),
        description: 'This is not the installation date but the registration date in our systems'
    },
    /* { field: "todate", headerName: "To", flex: 1 }, */
    createVersionColumn("commercialVersion", "Version"),
    createVersionColumn("lsvVersion", "LSupervisor"),
    createVersionColumn("lnestVersion", "LNest"),
    createVersionColumn("lscanVersion", "LScan"),
    createVersionColumn("lmchVersion", "LCMachine"),
    createVersionColumn("offloadVersion", "Offload"),
    /* { field: "ldbSrvVersion", headerName: "DB", flex: 1 }, */
    createVersionColumn("dataManagerVersion", "DataMgr"),
    createVersionColumn("reportsManagerVersion", "ReportMgr")
]

export const hardwareColumns = [
    // { field: "serialNumber", headerName: "serialNumber", flex: 1 },
    // { field: "rowNumber", headerName: "rowNumber", flex: 1 },
    // { field: "serialNumber", headerName: "serialNumber", flex: 1 },
    {
        field: "fromDate", headerName: "Registered at", flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value),
        description: 'This is not the hardware change date but the registration date in our systems'
    },
    /* { field: "todate", headerName: "To", flex: 1 }, */
    { field: "machine", headerName: "Pilot/CMachine", flex: 1 },
    // { field: "machineType", headerName: "machineType", flex: 1 },
    { field: "bibliobusMachine", headerName: "Bibliobus", flex: 1 },
    { field: "eventManagerMachine", headerName: "EventManager", flex: 1 },
    { field: "shared", headerName: "Server ?", flex: 1, valueFormatter: (value:number)=>formatShared(value) },
    // { field: "model", headerName: "model", flex: 1 },
    // { field: "market", headerName: "market", flex: 1 },
    // { field: "product", headerName: "product", flex: 1 },
    // { field: "size", headerName: "size", flex: 1 },
    // { field: "variant", headerName: "variant", flex: 1 }


];

export const contractColumns = [
    /* { field: "rowNumber", headerName: "Rank", flex: 1, }, */
    { field: "fromDate", headerName: "Date", minWidth: 80, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    { field: "todate", headerName: "To", flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    /* { field: "mainFeatureHostID", headerName: "HostID", flex: 1 }, */
    { field: "contract", headerName: "Contract", flex: 1 },
    { field: "isactive", headerName: "Active", flex: 1 },
]

export const dashboardColumns = [
    { field: "yearWeek", headerName: "Week", maxWidth: 80, flex: 1 },
    { field: "eventEmittedDate", headerName: "Last event", minWidth: 60, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    { field: "eventReceivedDate", headerName: "Reported on", minWidth: 60, flex: 1, valueFormatter: (value: Date | undefined) => getFormatedStringFromDate(value) },
    { field: "cMachine", headerName: "CMachine", flex: 1 },
    { field: "pilot", headerName: "Pilot", flex: 1 },
    { field: "eventManager", headerName: "EventManager", flex: 1 },
]